import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { HelmetProvider } from 'react-helmet-async'
import brainChargeHeroImg from './assets/brainCharge-bottle-angle.png'
import { AppErrorBoundary } from './components/AppErrorBoundary'

// Inject a high-priority preload for the LCP hero image so the browser can
// discover and fetch it before React renders the <img>. This is the standard
// fix for Lighthouse's "LCP request discovery" audit.
if (typeof document !== 'undefined' && !document.head.querySelector('link[data-lcp-hero]')) {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = brainChargeHeroImg;
  link.fetchPriority = 'high';
  link.setAttribute('data-lcp-hero', 'true');
  document.head.appendChild(link);
}

// Start fetching reviews_public before React renders — breaks the critical
// request chain so the Supabase call runs in parallel with lazy JS chunks.
import('./lib/prefetchReviews').then(({ startReviewsPrefetch }) => startReviewsPrefetch());

// Defer non-critical work (pixels, service worker cleanup, profiler) until
// after the browser is idle so it doesn't compete with initial render.
const onIdle = typeof requestIdleCallback === 'function'
  ? requestIdleCallback
  : (cb: () => void) => setTimeout(cb, 1);

onIdle(() => {
  // Ad pixels — initPixels() is a no-op unless the visitor has already
  // accepted marketing cookies; the consent banner calls it on acceptance.
  import('./lib/pixels').then(({ initPixels }) => initPixels());

  // Reflow profiler — no-op unless ?profile=reflow
  import('./lib/reflowProfiler').then(({ installReflowProfiler }) => installReflowProfiler());

  // Service worker cleanup
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => r.unregister());
    });
    caches.keys().then((keys) => {
      keys.forEach((key) => caches.delete(key));
    }).catch(() => undefined);
  }

  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('_boot')) {
      url.searchParams.delete('_boot');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    }
  } catch { /* ignore */ }

  if (window.location.pathname === '/index') {
    window.history.replaceState(null, '', '/');
  }
});

createRoot(document.getElementById("root")!).render(
  <AppErrorBoundary>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </AppErrorBoundary>
);

// Tell the index.html boot watchdog that React started.
try { (window as unknown as { __protonBooted?: boolean }).__protonBooted = true; } catch { /* ignore */ }
